import { supabase } from '@/integrations/supabase/client';

export interface GateConfig {
  context_text: string;
  header_text: string;
  subheader_text: string;
  free_title: string;
  free_bullets: string[];
  pro_title: string;
  pro_subheader: string;
  pro_save_money: string[];
  pro_make_money: string[];
  pro_save_time: string[];
  pro_bullets: string[]; // Deprecated - kept for backwards compatibility
  pro_cta_label: string;
  footer_text: string;
  // Visual anchors
  trust_badge: string;
  quick_stat: string;
  free_cta_subtext: string;
  pro_cta_subtext: string;
  micro_reviews: Array<{
    text: string;
    author?: string;
  }>;
  tool_logos: Array<{
    name: string;
    logo_url: string;
  }>;
}

/**
 * Fetch product gate configuration from system_config
 * Follows playbook pattern: data facade layer
 */
export async function getGateConfig(): Promise<GateConfig> {
  const { data, error } = await supabase
    .from('system_config')
    .select('value')
    .eq('key', 'product_gate_config')
    .single();

  const defaultConfig: GateConfig = {
    context_text: "Create a free account to keep viewing product details",
    header_text: "Stop guessing. We did the homework.",
    subheader_text: "We did the homework. You get the clarity. Everything organized side by side so you save time, skip the sales pitch, and buy only what actually moves your business forward.",
    free_title: "Free Account",
    free_bullets: [
      "See full product details that vendors usually bury",
      "Unlock side-by-side comparisons to save time",
      "Get transparent breakdowns without sales calls",
      "Make smarter buying decisions in seconds"
    ],
    pro_title: "Circle Pro",
    pro_subheader: "Unlock the full power of Circle and get the advantage agents wish they had years ago.",
    pro_save_money: [
      "Member-only pricing on select vendors",
      "Circle Points™ cash-back on tools you're already buying",
      "Smart AI price analysis so you always get the best deal"
    ],
    pro_make_money: [
      "Create and sell your Agent Playbook in the Marketplace",
      "Earn referral overrides when other agents join and buy tools",
      "Get credit on every referral — even on a free plan"
    ],
    pro_save_time: [
      "Full comparison engine (AI-powered)",
      "Unlimited Ask Circle recommendations",
      "Personalized tech stack builder matched to your goals",
      "Access to templates, playbooks & Academy content",
      "Priority support & early access to new vendors"
    ],
    pro_bullets: [], // Deprecated
    pro_cta_label: "See Everything You Unlock",
    footer_text: "Start free. Upgrade anytime.",
    trust_badge: "",
    quick_stat: "Agents usually find a cheaper or better option within seconds.",
    free_cta_subtext: "Takes 10 seconds. No credit card.",
    pro_cta_subtext: "",
    micro_reviews: [],
    tool_logos: [
      { name: "Chime", logo_url: "" },
      { name: "KVCore", logo_url: "" },
      { name: "Follow Up Boss", logo_url: "" },
      { name: "Sierra Interactive", logo_url: "" },
      { name: "Canva", logo_url: "" }
    ]
  };

  if (error || !data) {
    console.error('Error fetching gate config:', error);
    return defaultConfig;
  }

  // Validate and return config
  try {
    const config = data.value as any;
    return {
      context_text: config.context_text || defaultConfig.context_text,
      header_text: config.header_text || defaultConfig.header_text,
      subheader_text: config.subheader_text || defaultConfig.subheader_text,
      free_title: config.free_title || defaultConfig.free_title,
      free_bullets: Array.isArray(config.free_bullets) ? config.free_bullets : defaultConfig.free_bullets,
      pro_title: config.pro_title || defaultConfig.pro_title,
      pro_subheader: config.pro_subheader || defaultConfig.pro_subheader,
      pro_save_money: Array.isArray(config.pro_save_money) ? config.pro_save_money : defaultConfig.pro_save_money,
      pro_make_money: Array.isArray(config.pro_make_money) ? config.pro_make_money : defaultConfig.pro_make_money,
      pro_save_time: Array.isArray(config.pro_save_time) ? config.pro_save_time : defaultConfig.pro_save_time,
      pro_bullets: Array.isArray(config.pro_bullets) ? config.pro_bullets : defaultConfig.pro_bullets,
      pro_cta_label: config.pro_cta_label || defaultConfig.pro_cta_label,
      footer_text: config.footer_text || defaultConfig.footer_text,
      trust_badge: config.trust_badge || defaultConfig.trust_badge,
      quick_stat: config.quick_stat || defaultConfig.quick_stat,
      free_cta_subtext: config.free_cta_subtext || defaultConfig.free_cta_subtext,
      pro_cta_subtext: config.pro_cta_subtext || defaultConfig.pro_cta_subtext,
      micro_reviews: Array.isArray(config.micro_reviews) ? config.micro_reviews : defaultConfig.micro_reviews,
      tool_logos: Array.isArray(config.tool_logos) ? config.tool_logos : defaultConfig.tool_logos,
    };
  } catch {
    return defaultConfig;
  }
}
